import { $httpFhirApi } from '@/common/api/httpFhir.service';
import timezoneApi from '@/common/api/timezone.api';
import { getUrlFromCanonical, isHtml } from '@/common/core';
import { crud } from '@/common/crud';
import { useAppointmentFlow } from '@/common/workflowEngine/useAppointmentFlow';
import { useQuestionnaireFlow } from '@/common/workflowEngine/useQuestionnaireFlow';
import { useTaskFlow } from '@/common/workflowEngine/useTaskFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useDevOnly } from '@/composables/useDevOnly';
import { CUSTOM_PROBLEM_STATUS_OPTIONS } from '@/composables/useTreatmentPlanUtils';
import { Encounter, EncounterDiagnosis, Procedure, QuestionnaireResponse, QuestionnaireResponseItem } from '@/fhirworks';
import { useAuthStore } from '@/stores/auth';
import { addDays, addMinutes } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';

const { isProfile, isBnSupport } = useDevOnly();
const canSeeDraftForms = () => isBnSupport() || isProfile();

const { postTransaction } = useWorkflow();
const { addTask: addTaskFlow, deleteTask } = useTaskFlow();

function getProviderFromAuth() {
    const authStore = useAuthStore();
    return authStore.aidboxPersonReference;
}

function createProviderParticipant(provider) {
    return [
        {
            type: [{ coding: [{ code: 'PPRF' }] }],
            individual: {
                id: provider.id,
                resourceType: provider.resourceType,
                display: provider?.display,
            },
        },
    ];
}

export async function getLocationPosCode(locationId) {
    if (!locationId) return undefined;
    const locationResource = (await $httpFhirApi.get(`/Location/${locationId}`)).data;
    return locationResource?.pos?.coding?.[0]?.code;
}

export function getDefaultSupervisorParticipant() {
    const authStore = useAuthStore();
    const supervisor = authStore.user?.user?.supervisingProvider;
    if (!supervisor?.id) return null;
    return {
        type: [{ coding: [{ code: 'supervisor', display: 'supervisor' }] }],
        individual: {
            id: supervisor.id,
            resourceType: 'Practitioner',
            display: supervisor.display || supervisor.fullName,
        },
    };
}

function getCurrentStartDate() {
    return timezoneApi.getGlobalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", timezoneApi.getLocalTimeZone());
}

function getTaskDates() {
    return {
        start: new Date().toISOString(),
        end: addDays(new Date(), 7).toISOString(),
    };
}

async function beginAdhocEncounter(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    let encounterType = '';
    let encounterTitle = data.name || 'Session';
    let encounterTypeId = data.type?.id;

    if (encounterTypeId) {
        encounterType = (await $httpFhirApi.get('/BN_EncounterType/' + encounterTypeId)).data;
        encounterTitle = encounterType?.title;
    }
    // this flow can be applied to a single patient or all patients
    // if no patients in appointment we can exit
    if (!data.patient) return [];

    // set provider and assignees
    const provider = getProviderFromAuth();

    const startDate = getCurrentStartDate();
    const encounter = {
        start: startDate,
        end: data.form?.isStandAloneForm ? null : addMinutes(new Date(startDate), encounterType?.defaultDuration).toISOString(),
    };
    encounter.location = data.program?.location ? [{ location: data.program.location }] : [{ location: { display: 'Unknown' } }];
    const encounterSetting = await getLocationPosCode(data.program?.location?.id);
    encounter.provider = createProviderParticipant(provider);
    const supervisorParticipant = getDefaultSupervisorParticipant();
    if (supervisorParticipant) encounter.provider.push(supervisorParticipant);

    // get current diagnosis for patients
    let patientDiagnosis = (await $httpFhirApi.get('/Condition?subject=' + data.patient?.id + '&clinical-status=active&category=encounter-diagnosis')).data.map(
        (cond) => new EncounterDiagnosis({ condition: cond }),
    );

    // set up the encounter resource for each patient
    let encounterPartof = undefined;
    // If user selected a program, use the Encounter that was associated with that program
    if (data.encounterPartOf) {
        encounterPartof = data.encounterPartOf;
    } else {
        encounterPartof = (await $httpFhirApi.get('/Encounter?subject=' + data.patient?.id + '&healthcareService=' + data.program.id + '&status:not=entered-in-error,finished,cancelled')).data[0];
    }
    let type = null;
    if (encounterType) {
        type = [
            {
                coding: [{ system: 'http://fhir.bestnotes.com/encounterTypeToken', code: encounterType.token, display: encounterTitle }],
            },
            {
                coding: [{ system: 'http://fhir.bestnotes.com/encounterTypeUrl', code: encounterType.url }],
            },
        ];
    }
    if (data.type?.text === 'standAloneForm') {
        type = [{ text: 'standAloneForm', coding: [{ system: 'http://fhir.bestnotes.com/standAloneForm', display: encounterTitle }] }];
    }

    const encounterResource = new Encounter({
        id: uuidv4(),
        status: 'in-progress',
        class: { code: 'AMB', display: 'ambulatory' },
        type: type,
        subject: { id: data.patient?.id, resourceType: data.patient?.resourceType },
        diagnosis: patientDiagnosis,
        participant: encounter.provider,
        period: { end: encounter.end, start: encounter.start },
        location: encounter.location,
        setting: encounterSetting,
        episodeOfCare: encounterPartof?.episodeOfCare,
        partOf: { id: encounterPartof?.id, resourceType: 'Encounter' },
    });

    transaction.push(crud.post(encounterResource));

    const { start, end } = getTaskDates();

    transaction = await addTask(
        {
            task: {
                owner: provider,
                for: data.patient,
                start: start,
                end: end,
                assignee: [provider],
            },
            encounterType: data.type?.text === 'standAloneForm' ? null : encounterType,
            encounter: encounterResource,
            form: data.form,
        },
        transaction,
    );

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function beginAdhocGroupEncounter(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const encounterTitle = data.name || 'Session';

    // Require patients array for group encounters
    const patients = Array.isArray(data.patients) ? data.patients : [];
    if (!patients.length) {
        return [];
    }

    // set provider and assignees (ad hoc - use current user)
    const provider = getProviderFromAuth();
    const assignee = [provider];

    // Generate group encounter task ID
    const groupEncounterTaskId = uuidv4();

    // Use current time for ad hoc encounters, always set end to null
    const startDate = getCurrentStartDate();
    const encounterPeriod = {
        start: startDate,
        end: null,
    };

    const encounterLocation = data.location ? [{ location: data.location }] : null;
    const encounterSetting = await getLocationPosCode(data.location?.id);
    const encounterProvider = createProviderParticipant(provider);
    const supervisorParticipant = getDefaultSupervisorParticipant();
    if (supervisorParticipant) encounterProvider.push(supervisorParticipant);

    // Get current diagnosis for all patients
    const patientIds = patients.map((p) => p.id || p.actor?.id).join(',');
    const diagnosisResult = await $httpFhirApi.get('/Condition?subject=' + patientIds + '&clinical-status=active&category=encounter-diagnosis');
    const diagnosis = Array.isArray(diagnosisResult.data) ? diagnosisResult.data : [];

    // Build encounter type coding - always use standAloneForm for ad hoc group encounters
    const type = [{ text: 'standAloneForm', coding: [{ system: 'http://fhir.bestnotes.com/standAloneForm', display: encounterTitle }] }];

    // Prepare forms array
    const forms = data.form ? [data.form] : [];
    const formIdMap = forms.map(() => uuidv4());

    // Fetch encounterPartOf for all patients in parallel
    const encounterPartOfMap = new Map();
    if (data.encounterPartOf) {
        // If encounterPartOf is provided, use it for all patients
        patients.forEach((patient) => {
            encounterPartOfMap.set((patient.actor || patient).id, data.encounterPartOf);
        });
    } else {
        // Fetch encounterPartOf for each patient using their specific program
        const encounterPartOfResults = await Promise.all(
            patients.map(async (patient) => {
                const patientId = (patient.actor || patient).id;
                const programId = (patient.actor || patient).healthcareService?.id;
                if (programId) {
                    const result = await $httpFhirApi.get('/Encounter?subject=' + patientId + '&healthcareService=' + programId + '&status:not=entered-in-error,finished,cancelled');
                    return { patientId, encounterPartOf: result.data[0] };
                }
                return { patientId, encounterPartOf: null };
            }),
        );
        encounterPartOfResults.forEach(({ patientId, encounterPartOf }) => {
            if (encounterPartOf) {
                encounterPartOfMap.set(patientId, encounterPartOf);
            }
        });
    }

    // Collect all encounter resources and task data per patient
    const patientData = [];
    const { start, end } = getTaskDates();
    const flowAssignee = null;

    for (let patientIndex = 0; patientIndex < patients.length; patientIndex++) {
        const patient = patients[patientIndex];
        // Normalize patient reference (handle both {id, resourceType} and {actor: {id, resourceType}} formats)
        const patientRef = patient.actor || patient;
        const patientId = patientRef.id;

        const patientDiagnosis = diagnosis.filter((cond) => cond.subject?.id === patientId).map((cond) => new EncounterDiagnosis({ condition: cond }));

        // Get encounterPartOf from the map (already fetched in parallel above)
        const encounterPartOf = encounterPartOfMap.get(patientId);

        // Use the provided group location for all patients
        const patientEncounterLocation = encounterLocation;
        const episodeOfCareArray = encounterPartOf?.episodeOfCare ? (Array.isArray(encounterPartOf.episodeOfCare) ? encounterPartOf.episodeOfCare : [encounterPartOf.episodeOfCare]) : undefined;
        // Create encounter resource for this patient
        const encounterResource = new Encounter({
            id: uuidv4(),
            status: 'in-progress',
            class: { code: 'AMB', display: 'ambulatory' },
            type: type,
            subject: { id: patientId, resourceType: patientRef.resourceType || 'Patient' },
            diagnosis: patientDiagnosis,
            participant: encounterProvider,
            period: encounterPeriod,
            location: patientEncounterLocation,
            setting: encounterSetting,
            episodeOfCare: episodeOfCareArray,
            partOf: encounterPartOf ? { id: encounterPartOf.id, resourceType: 'Encounter' } : undefined,
        });

        // Collect task data for all forms
        const patientTaskData = forms.flatMap((form, formIndex) => {
            const tasks = [];
            // Create main group task once per form (only on first patient)
            if (patientIndex === 0) {
                tasks.push({
                    data: {
                        task: {
                            id: formIdMap[formIndex],
                            owner: provider,
                            start: start,
                            end: end,
                            assignee: assignee,
                            groupTask: groupEncounterTaskId,
                            parentGroupTask: groupEncounterTaskId,
                        },
                        form: form,
                    },
                });
            }
            // Create participant task for this patient
            tasks.push({
                data: {
                    task: {
                        owner: provider,
                        for: patientRef,
                        start: start,
                        end: end,
                        assignee: flowAssignee,
                        groupTask: groupEncounterTaskId,
                        groupParticipantTask: formIdMap[formIndex],
                    },
                    form: form,
                    encounter: encounterResource,
                },
            });
            return tasks;
        });

        // Store patient data with task queue
        patientData.push({
            patientRef,
            encounterResource,
            patientIndex,
            taskData: patientTaskData,
        });
    }

    // Process each patient's tasks and transactions
    const { addFormTask } = useAppointmentFlow();
    const results = {};

    const processPatient = async (patientItem) => {
        const patientTransaction = existingTransaction ? transaction : [];

        patientTransaction.push(crud.post(patientItem.encounterResource));

        // Process all task data for this patient sequentially
        await patientItem.taskData.reduce(async (taskPromise, taskItem) => {
            await taskPromise;
            return await addFormTask(taskItem.data, patientTransaction);
        }, Promise.resolve());

        const result = {
            patient: patientItem.patientRef,
            encounter: patientItem.encounterResource,
            groupTaskId: groupEncounterTaskId,
        };

        if (existingTransaction === false) {
            result.results = await postTransaction(patientTransaction);
        }

        return {
            patientIndex: patientItem.patientIndex.toString(),
            result,
        };
    };

    // Process patients: parallel when transactions are independent, sequential when accumulating
    if (existingTransaction === false) {
        // Each patient's transaction is independent - process in parallel for better performance
        const patientResults = await Promise.all(patientData.map(processPatient));
        patientResults.forEach(({ patientIndex, result }) => {
            results[patientIndex] = result;
        });
    } else {
        // Must process sequentially when accumulating into shared transaction
        await patientData.reduce(async (previousPromise, patientItem) => {
            await previousPromise;
            const { patientIndex, result } = await processPatient(patientItem);
            results[patientIndex] = result;
        }, Promise.resolve());
    }

    if (existingTransaction === false) {
        return results;
    }

    return transaction;
}

function wrapTextIfNeeded(textValue) {
    return textValue && !isHtml(textValue) ? `<span>${textValue}</span>` : textValue;
}

function filterByResourceType(data, resourceType) {
    return data.filter((item) => item.resourceType === resourceType);
}

function findResourceById(data, resourceType, id) {
    return data.find((item) => item.resourceType === resourceType && item.id === id);
}

// Helper function to create a map from an array using a key function
// If multiple is true, values are arrays; otherwise, last value overwrites previous ones
function createMapFromArray(array, getKey, getValue = (item) => item, multiple = true) {
    const map = new Map();
    array.forEach((item) => {
        const key = getKey(item);
        if (key) {
            if (multiple) {
                if (!map.has(key)) {
                    map.set(key, []);
                }
                map.get(key).push(getValue(item));
            } else {
                map.set(key, getValue(item));
            }
        }
    });
    return map;
}

function createResponseItemsFromQuestionnaire(questionnaire) {
    const responseItems = [];
    questionnaire.item.forEach((item) => {
        if (item.type === 'group') {
            item.item.forEach((sectionItem) => {
                if (sectionItem.type === 'question') {
                    // Fieldset: group -> question -> item
                    const fieldSetResponseItem = new QuestionnaireResponseItem({ linkId: sectionItem.linkId });
                    const innerItems = [];
                    sectionItem.item.forEach((fieldSetItem) => {
                        if (['display'].includes(fieldSetItem.type)) return;
                        const fieldItem = new QuestionnaireResponseItem({ linkId: fieldSetItem.linkId });
                        const textValue = fieldSetItem?._text?.renderingXhtml || fieldSetItem.text;
                        fieldItem.bnResponseText = wrapTextIfNeeded(textValue);
                        innerItems.push(fieldItem);
                    });
                    fieldSetResponseItem.item = innerItems;
                    responseItems.push(fieldSetResponseItem);
                } else {
                    // Regular section in group
                    const sectionResponseItem = new QuestionnaireResponseItem({ linkId: sectionItem.linkId });
                    const textValue = sectionItem?._text?.renderingXhtml || sectionItem.text;
                    sectionResponseItem.bnResponseText = wrapTextIfNeeded(textValue);
                    responseItems.push(sectionResponseItem);
                }
            });
        } else if (item.type === 'question') {
            // Question with nested items
            const fieldSetResponseItem = new QuestionnaireResponseItem({ linkId: item.linkId });
            const innerItems = [];
            item.item.forEach((fieldSetItem) => {
                if (['display'].includes(fieldSetItem.type)) return;
                const fieldItem = new QuestionnaireResponseItem({ linkId: fieldSetItem.linkId });
                const textValue = fieldSetItem?._text?.renderingXhtml || fieldSetItem.text;
                fieldItem.bnResponseText = wrapTextIfNeeded(textValue);
                innerItems.push(fieldItem);
            });
            fieldSetResponseItem.item = innerItems;
            responseItems.push(fieldSetResponseItem);
        } else {
            // Regular item
            const responseItem = new QuestionnaireResponseItem({ linkId: item.linkId });
            const textValue = item?._text?.renderingXhtml || item.text;
            responseItem.bnResponseText = wrapTextIfNeeded(textValue);
            responseItems.push(responseItem);
        }
    });
    return responseItems;
}

async function addParticipantToGroupEncounter(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const patients = Array.isArray(data.patients) ? data.patients : [];
    if (!patients.length) {
        return transaction;
    }

    // Only include Encounters (needed for period/location/type) — QRs and Patients aren't used here.
    const groupTasksResult = await $httpFhirApi.get(`Task?groupTask=${data.groupTaskId}&_include=Task:encounter:Encounter`);

    const groupTasks = filterByResourceType(groupTasksResult.data, 'Task');

    // Find main group tasks (one per form) - these don't have a 'for' field and have a code
    // Exclude assignment tasks (code='assignment') and tasks with assignment types in version field
    const assignmentTaskTypes = ['author', 'editor', 'viewer', 'signature', 'review'];
    const mainGroupTasks = groupTasks.filter(
        (task) =>
            !task.for &&
            task.code?.coding?.[0]?.code &&
            task.code?.coding?.[0]?.code !== 'assignment' &&
            task.code?.coding?.[0]?.version &&
            !assignmentTaskTypes.includes(task.code?.coding?.[0]?.version),
    );

    // Extract forms from main group tasks
    const forms = mainGroupTasks
        .map((task) => {
            const formUrl = task.code?.coding?.[0]?.version;
            return formUrl ? { url: formUrl } : null;
        })
        .filter(Boolean);
    const formIdMap = mainGroupTasks.map((task) => task.id);

    // Get encounter details from first participant task
    const participantTask = groupTasks.find((task) => task.for && task.encounter);
    if (!participantTask || !participantTask.encounter) {
        throw new Error('No participant task with encounter found in group encounter');
    }
    const firstEncounter = findResourceById(groupTasksResult.data, 'Encounter', participantTask.encounter.id);

    const authStore = useAuthStore();
    const provider = authStore.aidboxPersonReference;

    // Get period from a formEncounter (these always have the initial group start/end date)
    // Fallback to firstEncounter if formEncounter doesn't exist (form doesn't have session widget)
    const allEncounters = filterByResourceType(groupTasksResult.data, 'Encounter');
    const baseEncounter = allEncounters.find((enc) => enc.type?.[0]?.text === 'formEncounter') || firstEncounter;

    const encounterPeriod = {
        start: baseEncounter.period?.start || null,
        end: baseEncounter.period?.end || null,
    };

    const encounterLocation = firstEncounter.location || [{ location: { display: 'Unknown' } }];
    const encounterSetting = firstEncounter.setting || (await getLocationPosCode(firstEncounter.location?.[0]?.location?.id));
    let encounterProvider = firstEncounter.participant;
    if (!encounterProvider) {
        encounterProvider = [
            {
                type: [{ coding: [{ code: 'PPRF' }] }],
                individual: {
                    id: provider.id,
                    resourceType: provider.resourceType,
                    display: provider?.display,
                },
            },
        ];
        const supervisorParticipant = getDefaultSupervisorParticipant();
        if (supervisorParticipant) encounterProvider.push(supervisorParticipant);
    }

    // Build encounter type coding (reuse from existing encounter)
    const type = firstEncounter.type;

    // Determine if this is an ad hoc group encounter (no appointment) or appointment-based
    const isAdhocGroupEncounter = !firstEncounter.appointment || firstEncounter.appointment.length === 0;

    const normalizedPatients = patients.map((patient) => {
        const patientRef = patient.actor || patient;
        // Use patient's selected program from dialog
        const patientProgram =
            patient.healthcareService ||
            (patient.selectedProgram ? { id: patient.selectedProgram.id, resourceType: patient.selectedProgram.resourceType, display: patient.selectedProgram.display } : null);
        return { original: patient, ref: patientRef, id: patientRef.id, program: patientProgram };
    });

    if (!normalizedPatients.length) {
        return existingTransaction === false ? [] : transaction;
    }

    // Fetch questionnaires, diagnosis/encounterPartOf, and appointment in parallel
    const patientIds = normalizedPatients.map((p) => p.id).join(',');

    const questionnaireMapPromise = (async () => {
        const map = new Map();
        if (forms.length > 0) {
            const results = await Promise.all(
                forms.map(async (form) => {
                    const { fullUrl } = getUrlFromCanonical(form.url);
                    const statusQuery = canSeeDraftForms() ? 'status=active,draft' : 'status=active';
                    const questionnaireResult = await $httpFhirApi.get(fullUrl + '&' + statusQuery + '&_elements=item,title,version,url,formType,_text');
                    return { formUrl: form.url, questionnaire: questionnaireResult.data[0] };
                }),
            );
            results.forEach(({ formUrl, questionnaire }) => map.set(formUrl, questionnaire));
        }
        return map;
    })();

    const diagnosisAndEncounterPromise = (async () => {
        const [diagnosisResult, encounterPartOfResults] = await Promise.all([
            patientIds ? $httpFhirApi.get('/Condition?subject=' + patientIds + '&clinical-status=active&category=encounter-diagnosis') : Promise.resolve({ data: [] }),
            Promise.all(
                normalizedPatients.map(async ({ id: patientId, program: patientProgram }) => {
                    const programToUse = patientProgram?.id;
                    if (!programToUse) {
                        return { patientId, encounterPartOf: null, episodeOfCare: null, programDisplay: null };
                    }
                    const queryUrl =
                        '/Encounter?subject=' + patientId + '&healthcareService=' + programToUse + '&status:not=entered-in-error,finished,cancelled' + '&_include=episode-of-care:EpisodeOfCare';
                    const result = await $httpFhirApi.get(queryUrl);
                    const encounter = result.data.find((r) => r.resourceType === 'Encounter') || null;
                    const episodeOfCare = encounter?.episodeOfCare ? result.data.find((r) => r.resourceType === 'EpisodeOfCare' && encounter.episodeOfCare.some((ref) => ref.id === r.id)) : null;
                    let programDisplay = null;
                    if (encounter?.healthcareService?.id) {
                        const hsResult = await $httpFhirApi.get('HealthcareService/' + encounter.healthcareService.id);
                        const healthcareService = Array.isArray(hsResult.data) ? hsResult.data[0] : hsResult.data;
                        programDisplay = healthcareService?.name ?? encounter.healthcareService?.display ?? null;
                    }
                    return { patientId, encounterPartOf: encounter, episodeOfCare: episodeOfCare, programDisplay };
                }),
            ),
        ]);
        return { diagnosisResult, encounterPartOfResults };
    })();

    const appointmentPromise = (async () => {
        if (!isAdhocGroupEncounter && firstEncounter.appointment?.[0]?.id) {
            const appointmentResult = await $httpFhirApi.get(`Appointment/${firstEncounter.appointment[0].id}`);
            return { appointment: appointmentResult.data, encounterType: null };
        } else if (isAdhocGroupEncounter) {
            const encounterTypeUrl = firstEncounter.type?.find((t) => t.coding?.[0]?.system === 'http://fhir.bestnotes.com/encounterTypeUrl')?.coding?.[0]?.code;
            const encounterType = encounterTypeUrl ? { id: encounterTypeUrl.split('/').pop(), resourceType: 'BN_EncounterType' } : null;
            return { appointment: null, encounterType: encounterType };
        }
        return { appointment: null, encounterType: null };
    })();

    const [questionnaireMap, { diagnosisResult, encounterPartOfResults }, { appointment, encounterType }] = await Promise.all([
        questionnaireMapPromise,
        diagnosisAndEncounterPromise,
        appointmentPromise,
    ]);

    const diagnosis = diagnosisResult.data || [];
    const diagnosisMap = createMapFromArray(diagnosis, (cond) => cond.subject?.id);

    const encounterPartOfMap = new Map();
    const episodeOfCareMap = new Map();
    const programDisplayMap = new Map();
    encounterPartOfResults.forEach(({ patientId, encounterPartOf, episodeOfCare, programDisplay }) => {
        encounterPartOfMap.set(patientId, encounterPartOf);
        if (episodeOfCare) {
            episodeOfCareMap.set(patientId, episodeOfCare);
        }
        if (programDisplay) {
            programDisplayMap.set(patientId, programDisplay);
        }
    });

    // Process each new patient
    const formTaskPromises = [];
    const newParticipantEncounters = [];
    const newParticipantEncountersMap = new Map(); // Map patientId to encounter resource
    for (const { ref: patientRef, id: patientId } of normalizedPatients) {
        const patientDiagnosis = (diagnosisMap.get(patientId) || []).map((cond) => new EncounterDiagnosis({ condition: cond }));
        const encounterPartOf = encounterPartOfMap.get(patientId);
        const episodeOfCare = episodeOfCareMap.get(patientId);

        // Create encounter resource for this patient
        const encounterResource = new Encounter({
            id: uuidv4(),
            status: 'in-progress',
            class: { code: 'AMB', display: 'ambulatory' },
            type: type,
            subject: { id: patientId, resourceType: patientRef.resourceType, display: patientRef.display || patientRef.fullName },
            diagnosis: patientDiagnosis,
            participant: encounterProvider,
            period: encounterPeriod,
            location: encounterLocation,
            setting: encounterSetting,
            episodeOfCare: episodeOfCare ? [{ id: episodeOfCare.id, resourceType: 'EpisodeOfCare' }] : undefined,
            partOf: encounterPartOf
                ? {
                      id: encounterPartOf.id,
                      resourceType: 'Encounter',
                      display: encounterPartOf.display || encounterPartOf.healthcareService?.display,
                  }
                : undefined,
        });

        // For appointment-based encounters, add appointment reference with display
        if (appointment) {
            const appointmentTitle = appointment.description || appointment.bnAppointmentType?.display || 'Session';
            encounterResource.appointment = [{ id: appointment.id, display: appointmentTitle, resourceType: 'Appointment' }];
        }

        transaction.push(crud.post(encounterResource));
        newParticipantEncounters.push({ patientId, patientRef, encounter: encounterResource });
        newParticipantEncountersMap.set(patientId, encounterResource);

        // Create participant tasks for each form, passing pre-fetched questionnaire
        // metadata so addTask can skip redundant API calls
        const { addFormTask } = useAppointmentFlow();
        formTaskPromises.push(
            ...forms.map((form, formIndex) => {
                const mainTaskId = formIdMap[formIndex];
                const questionnaire = questionnaireMap.get(form.url);
                const enrichedForm = questionnaire ? { url: questionnaire.url, title: questionnaire.title, version: questionnaire.version, formType: questionnaire.formType } : form;
                return addFormTask(
                    {
                        task: {
                            owner: provider,
                            for: patientRef,
                            start: new Date().toISOString(),
                            end: addDays(new Date(), 7).toISOString(),
                            assignee: null,
                            groupTask: data.groupTaskId,
                            groupParticipantTask: mainTaskId,
                        },
                        form: enrichedForm,
                        encounter: encounterResource,
                        // Pass appointment for appointment-based encounters so tasks get appointment reference in basedOn
                        appointment: appointment,
                    },
                    transaction,
                );
            }),
        );
    }

    // Wait for all form tasks to be added to transaction
    await Promise.all(formTaskPromises);

    if (existingTransaction === false) {
        const results = await postTransaction(transaction);

        // Create QuestionnaireResponses in the background so the caller can return immediately.
        // The returned results.qrReady promise resolves when QRs are fully created and linked.
        const newPatientIdsSet = new Set(normalizedPatients.map((p) => p.id));

        if (newPatientIdsSet.size > 0) {
            const createQuestionnaireResponses = async () => {
                const { initializeQuestionnaire } = useQuestionnaireFlow();

                const newTasksResult = await $httpFhirApi.get(`Task?groupTask=${data.groupTaskId}&code=form&_include=Task:encounter:Encounter`);
                const newTasks = filterByResourceType(newTasksResult.data, 'Task').filter((item) => item.for && newPatientIdsSet.has(item.for.id));

                const questionnaireResults = newTasks
                    .map((task) => {
                        const formUrl = task.code?.coding?.[0]?.version;
                        const questionnaire = formUrl ? questionnaireMap.get(formUrl) : null;
                        return questionnaire ? { task, questionnaire } : null;
                    })
                    .filter(Boolean);

                const questionnaireResponseTransaction = [];
                for (const { task, questionnaire } of questionnaireResults) {
                    const responseItems = createResponseItemsFromQuestionnaire(questionnaire);
                    const patientEncounter = task.for?.id ? newParticipantEncountersMap.get(task.for.id) : undefined;

                    questionnaireResponseTransaction.push(
                        crud.post(
                            new QuestionnaireResponse({
                                questionnaire: questionnaire.url + '|' + questionnaire.version,
                                status: 'in-progress',
                                author: provider,
                                authored: timezoneApi.getGlobalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", timezoneApi.getLocalTimeZone()),
                                subject: task.for,
                                item: responseItems,
                                encounter: patientEncounter ? { id: patientEncounter.id, resourceType: 'Encounter' } : undefined,
                            }),
                        ),
                    );
                }

                if (questionnaireResponseTransaction.length > 0) {
                    const qrResults = await postTransaction(questionnaireResponseTransaction);

                    // Group form initialization by patient. initializeQuestionnaire dedups
                    // procedures by billing code against the session's already-saved procedures;
                    // running a single patient's forms in parallel would race that check and
                    // recreate duplicates. Patients stay parallel (dedup is per-patient), but a
                    // patient's forms are initialized sequentially so each procedure is committed
                    // before the next form's dedup query runs. See issue #4167.
                    const tasksByPatient = new Map();
                    for (const task of newTasks) {
                        if (!task.code?.coding?.[0]?.version || !task.for?.id) continue;
                        if (!tasksByPatient.has(task.for.id)) tasksByPatient.set(task.for.id, []);
                        tasksByPatient.get(task.for.id).push(task);
                    }

                    await Promise.all(
                        [...tasksByPatient.values()].map(async (patientTasks) => {
                            for (const task of patientTasks) {
                                const formUrl = task.code?.coding?.[0]?.version;
                                const savedQR = qrResults.find(
                                    (result) => result.resourceType === 'QuestionnaireResponse' && result.subject?.id === task.for?.id && result.questionnaire?.includes(formUrl),
                                );

                                if (!savedQR) continue;

                                const patientEncounter = task.for?.id ? newParticipantEncountersMap.get(task.for.id) : undefined;
                                if (!task.focus) {
                                    task.focus = { display: savedQR.questionnaire || '' };
                                } else if (!task.focus.display) {
                                    task.focus.display = savedQR.questionnaire || '';
                                }
                                // eslint-disable-next-line no-await-in-loop
                                await initializeQuestionnaire(
                                    {
                                        task: task,
                                        questionnaireResponse: savedQR,
                                        encounter: patientEncounter,
                                        service: appointment?.service,
                                        appointmentType: encounterType,
                                    },
                                    false,
                                );
                            }
                        }),
                    );
                }
            };
            results.qrReady = createQuestionnaireResponses();
        }

        return results;
    }

    return transaction;
}

async function removeParticipantFromGroupEncounter(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!data.groupTaskId || !data.patientId) {
        throw new Error('groupTaskId and patientId are required');
    }

    // Query for all tasks for this patient in the group encounter
    const tasksResult = await $httpFhirApi.get(`Task?groupTask=${data.groupTaskId}&_include=Task:focus:QuestionnaireResponse`);
    const patientTasks = filterByResourceType(tasksResult.data, 'Task').filter((task) => task.for?.id === data.patientId);

    const taskEncounterIds = new Set(patientTasks.map((task) => task.encounter?.id).filter(Boolean));
    const qrIds = new Set(patientTasks.map((task) => task.focus?.id).filter(Boolean));

    // Query for encounters (regular and formEncounter) linked to tasks, and procedures linked to those encounters
    const encounterResult = await $httpFhirApi.get(`Encounter?subject=${data.patientId}&_revinclude=Task:encounter&_revinclude=Encounter:part-of&_revinclude=Procedure:encounter:Encounter`);
    const allEncounters = filterByResourceType(encounterResult.data, 'Encounter');
    const allProcedures = filterByResourceType(encounterResult.data, 'Procedure');

    // Find all encounters for this patient that belong to this group encounter:
    // 1. Regular encounters linked to group tasks
    // 2. formEncounters whose partOf references a task-linked encounter
    // 3. Any other encounters for this patient linked to the same appointment
    const regularEncounterIds = new Set();
    const groupAppointmentId = allEncounters.find((enc) => taskEncounterIds.has(enc.id))?.appointment?.[0]?.id;

    const patientEncounters = allEncounters.filter((encounter) => {
        if (taskEncounterIds.has(encounter.id)) {
            regularEncounterIds.add(encounter.id);
            return true;
        }
        if (encounter.type?.[0]?.text === 'formEncounter' && regularEncounterIds.has(encounter.partOf?.id)) {
            return true;
        }
        if (groupAppointmentId && encounter.appointment?.[0]?.id === groupAppointmentId) {
            return true;
        }
        return false;
    });

    // Collect QuestionnaireResponse resources from task focus fields
    const questionnaireResponses = filterByResourceType(tasksResult.data, 'QuestionnaireResponse').filter((qr) => qrIds.has(qr.id));

    // Query and delete ClinicalImpressions for each QuestionnaireResponse
    const clinicalImpressionPromises = questionnaireResponses.map(async (qr) => {
        const clinicalImpressions = await $httpFhirApi.get(`ClinicalImpression?subject=${data.patientId}&supportingInfo=${qr.id}`);
        return { qr, clinicalImpressions: clinicalImpressions.data || [] };
    });
    const clinicalImpressionResults = await Promise.all(clinicalImpressionPromises);

    // Delete ClinicalImpressions
    clinicalImpressionResults.forEach(({ clinicalImpressions }) => {
        clinicalImpressions.forEach((ci) => transaction.push(crud.delete({ id: ci.id, resourceType: 'ClinicalImpression' })));
    });

    // Clean up Observations and ChargeItems sequentially for each QuestionnaireResponse before deleting them
    const { removeObservations, cleanupChargeItemsForQuestionnaireResponse } = useQuestionnaireFlow();
    transaction = await questionnaireResponses.reduce(async (txnPromise, qr) => {
        let txn = await txnPromise;
        txn = await removeObservations(qr, txn);
        // Get the encounter ID for this QuestionnaireResponse to pass to cleanupChargeItemsForQuestionnaireResponse
        const qrEncounterId = qr.encounter?.id || patientEncounters.find((enc) => enc.subject?.id === data.patientId)?.id;
        txn = await cleanupChargeItemsForQuestionnaireResponse(qr.id, qrEncounterId, txn, true);
        return txn;
    }, Promise.resolve(transaction));

    // Delete QuestionnaireResponses after cleaning up related resources
    questionnaireResponses.forEach((qr) => {
        transaction.push(crud.delete(qr));
    });

    // Delete procedures linked to patient encounters
    const patientEncounterIds = new Set(patientEncounters.map((enc) => enc.id));
    const patientProcedures = allProcedures.filter((procedure) => patientEncounterIds.has(procedure.encounter?.id));
    patientProcedures.forEach((procedure) => transaction.push(crud.delete(procedure)));

    // Delete tasks
    patientTasks.forEach((task) => transaction.push(crud.delete(task)));

    // Delete encounters in correct order: formEncounters first (they reference regular encounters via partOf),
    // then regular encounters to avoid orphaned references
    const formEncounters = patientEncounters.filter((encounter) => encounter.type?.[0]?.text === 'formEncounter');
    const regularEncounters = patientEncounters.filter((encounter) => encounter.type?.[0]?.text !== 'formEncounter');

    formEncounters.forEach((encounter) => transaction.push(crud.delete(encounter)));
    regularEncounters.forEach((encounter) => transaction.push(crud.delete(encounter)));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function createPortalPacket(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // this flow can be applied to a single patient or all patients
    // if no patients in appointment we can exit
    if (!data.patient) return [];

    const authStore = useAuthStore();

    // set provider and assignees
    const provider = authStore.aidboxPersonReference;

    const encounter = {
        start: timezoneApi.getGlobalDateTime(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", timezoneApi.getLocalTimeZone()),
    };
    encounter.location = [{ location: { display: 'Unknown' } }];
    encounter.provider = [
        {
            type: [{ coding: [{ code: 'PPRF' }] }],
            individual: {
                id: provider.id,
                resourceType: provider.resourceType,
                display: provider?.display,
            },
        },
    ];

    // set up the encounter resource for each patient
    let encounterPartof = (await $httpFhirApi.get('/Encounter?subject=' + data.patient?.id + '&healthcareService=' + data.program.id)).data[0];

    const encounterResource = new Encounter({
        id: uuidv4(),
        status: 'in-progress',
        class: { code: 'AMB', display: 'ambulatory' },
        type: [{ coding: [{ code: 'portalPacket', display: 'Portal packet' }] }],
        subject: { id: data.patient?.id, resourceType: data.patient?.resourceType },
        participant: encounter.provider,
        period: { start: encounter.start },
        location: encounter.location,
        // episodeOfCare: encounterPartof?.episodeOfCare,
        partOf: { id: encounterPartof?.id, resourceType: 'Encounter' },
    });

    transaction.push(crud.post(encounterResource));

    const start = new Date().toISOString();
    const end = addDays(new Date(), 7).toISOString();
    transaction = await addTask(
        {
            task: {
                owner: data.patient,
                for: data.patient,
                start: start,
                end: end,
                noAssignment: true,
            },
            encounter: encounterResource,
        },
        transaction,
    );

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

async function addTask(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    const task = data.task;

    if (data.encounter?.id) {
        task.encounter = { resourceType: 'Encounter', id: data.encounter.id };
    }
    const forms = data.encounterType?.form || [data.form];
    if (forms) {
        for (const form of forms) {
            task.form = form;
            transaction = await addTaskFlow(task, transaction);
        }
    } else {
        transaction = await addTaskFlow(task, transaction);
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

const updateEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!isEmpty(data.encounter)) transaction.push(crud.patch(data.encounter));
    if (!isEmpty(data.appointment)) transaction.push(crud.patch(data.appointment));

    if (!isEmpty(data.encounters) && Array.isArray(data.encounters)) {
        data.encounters.forEach((encounter) => {
            transaction.push(crud.patch(encounter));
        });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

// Resets finalizedInForm properties when forms are deleted to prevent treatment plan status issues
const cleanupFinalizedInForm = async (questionnaireResponseIds, patientId, transaction) => {
    if (!questionnaireResponseIds?.length || !patientId) {
        return transaction;
    }

    const matchesFinalizedInForm = (resource) => {
        if (!resource) return false;
        return resource.finalizedInForm && questionnaireResponseIds.includes(resource.finalizedInForm);
    };

    const extractResources = (response) => {
        return Array.isArray(response?.data) ? response.data.filter((resource) => resource != null) : [];
    };

    // Fetch all resources in parallel
    const [problemsRes, goalsRes, carePlansRes] = await Promise.all([
        $httpFhirApi.get(`Condition?subject=${patientId}&category=problem-list-item`),
        $httpFhirApi.get(`Goal?subject=${patientId}`),
        $httpFhirApi.get(`CarePlan?subject=${patientId}`),
    ]);

    // Reset problems (Conditions)
    extractResources(problemsRes).forEach((problem) => {
        if (matchesFinalizedInForm(problem)) {
            problem.clinicalStatus = CUSTOM_PROBLEM_STATUS_OPTIONS.find((el) => el?.coding?.[0]?.code === 'active');
            problem.verificationStatus = undefined;
            problem.finalizedInForm = undefined;
            transaction.push(crud.patch(problem));
        }
    });

    // Reset goals
    extractResources(goalsRes).forEach((goal) => {
        if (matchesFinalizedInForm(goal)) {
            goal.lifecycleStatus = 'active';
            goal.finalizedInForm = undefined;
            goal.resolvedDate = undefined;
            transaction.push(crud.patch(goal));
        }
    });

    // Reset objectives and their interventions
    extractResources(carePlansRes).forEach((carePlan) => {
        let needsUpdate = false;

        if (matchesFinalizedInForm(carePlan)) {
            carePlan.status = 'active';
            carePlan.finalizedInForm = undefined;
            if (carePlan.period?.end) {
                carePlan.period.end = undefined;
            }
            needsUpdate = true;
        }

        const validActivities = Array.isArray(carePlan.activity) ? carePlan.activity.filter((activity) => activity != null) : [];
        validActivities.forEach((intervention) => {
            if (intervention.detail && matchesFinalizedInForm(intervention.detail)) {
                intervention.detail.status = 'in-progress';
                intervention.detail.finalizedInForm = undefined;
                needsUpdate = true;
            }
        });

        if (needsUpdate) {
            transaction.push(crud.patch(carePlan));
        }
    });

    return transaction;
};

const removeEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let resources;

    if (!data?.appointment?.id) {
        resources = await $httpFhirApi.get(
            'Encounter?id=' +
                data.encounter.id +
                '&_include=appointment:Appointment&_revinclude=Task:encounter&_revinclude=Task:based-on:Appointment&_include=Task:focus:QuestionnaireResponse&_revinclude=Encounter:appointment:Appointment,Encounter:part-of:Encounter&_revinclude=Procedure:encounter:Encounter',
        );
        const validResources = Array.isArray(resources.data) ? resources.data.filter((item) => item != null) : [];
        data.appointment = validResources.find((item) => item.resourceType === 'Appointment');
    } else {
        resources = await $httpFhirApi.get(
            'Appointment?id=' +
                data.appointment.id +
                '&_revinclude=Task:based-on' +
                '&_revinclude=Encounter:appointment' +
                '&_revinclude=Procedure:encounter:Encounter' +
                '&_include=Task:focus:QuestionnaireResponse',
        );
    }

    // Filter out null/undefined entries from resources.data to prevent errors
    const validResources = Array.isArray(resources.data) ? resources.data.filter((item) => item != null) : [];

    // reset appointment
    if (data.appointment) {
        data.appointment.status = 'pending';
        // Clear cancelationReason so the reset PATCH satisfies FHIR constraint app-4
        // (cancelationReason may only exist when status is 'cancelled' or 'noshow').
        // This path bypasses updateEvent's defensive guard because crud.patch is
        // pushed directly here.
        data.appointment.cancelationReason = undefined;
        transaction.push(crud.patch(data.appointment));
    }

    // Get all unique patient IDs from encounters for cleanup (handles group encounters)
    const allEncounters = validResources.filter((item) => item.resourceType === 'Encounter');
    const patientIds = [
        ...new Set(
            allEncounters
                .map((enc) => enc.subject?.id)
                .filter(Boolean)
                .concat(data.encounter?.subject?.id ? [data.encounter.subject.id] : [], data.appointment?.participant?.filter((p) => p.actor?.resourceType === 'Patient').map((p) => p.actor.id) || []),
        ),
    ];

    // remove all encounters/tasks/forms/procedures/etc. associated with the appointment
    let nonAppointmentData = validResources.filter((item) => item.resourceType !== 'Appointment');

    // Collect QuestionnaireResponse IDs that will be deleted for cleanup
    const questionnaireResponseIds = [
        ...new Set([
            ...nonAppointmentData.filter((item) => item.resourceType === 'QuestionnaireResponse' && item.id).map((item) => item.id),
            ...nonAppointmentData.filter((item) => item.focus?.id && item.focus.resourceType === 'QuestionnaireResponse').map((item) => item.focus.id),
        ]),
    ];

    // Need to use for because the await call in the loop
    for (let resource of nonAppointmentData) {
        // make sure we don't add duplicates
        if (!transaction.find((e) => e.request.url.includes(resource.id) && e.request.method === 'DELETE')) {
            transaction.push(crud.delete(resource));
        }

        // removed referenced questionnaireResponses
        if (resource.focus?.id && resource.focus.resourceType === 'QuestionnaireResponse') {
            // We have a QuestionnaireResponse that needs to be deleted, look for corresponding
            // ClinicalImpression information and remove if found.
            if (resource.code?.coding?.[0]?.code === 'form' && resource?.for?.id) {
                let clinicalImpressions = await $httpFhirApi.get('ClinicalImpression?subject=' + resource.for.id + '&supportingInfo=' + resource.focus.id);
                const validClinicalImpressions = Array.isArray(clinicalImpressions.data) ? clinicalImpressions.data.filter((ci) => ci != null) : [];
                validClinicalImpressions.forEach((clinicalImpression) => {
                    transaction.push(crud.delete({ id: clinicalImpression.id, resourceType: 'ClinicalImpression' }));
                });
            }

            transaction.push(crud.delete(resource.focus));
        }
        if (resource.resourceType === 'QuestionnaireResponse') {
            const { removeObservations } = useQuestionnaireFlow();
            transaction = await removeObservations(resource, transaction);

            // Remove ChargeItems and authorization tracking for the QuestionnaireResponse
            const { cleanupChargeItemsForQuestionnaireResponse } = useQuestionnaireFlow();
            transaction = await cleanupChargeItemsForQuestionnaireResponse(resource.id, null, transaction);
        }
    }

    // Cleanup finalizedInForm properties for resources that were finalized in the deleted forms
    if (questionnaireResponseIds.length > 0 && patientIds.length > 0) {
        const cleanupResults = await Promise.all(patientIds.map((patientId) => cleanupFinalizedInForm(questionnaireResponseIds, patientId, [])));
        // Merge all transaction results into the main transaction
        cleanupResults.forEach((result) => {
            transaction.push(...result);
        });
    }

    // Delete AdverseEvents linked to any encounter being removed
    const encounterIds = [
        ...new Set(
            allEncounters
                .map((enc) => enc.id)
                .filter(Boolean)
                .concat(data.encounter?.id ? [data.encounter.id] : []),
        ),
    ];
    if (encounterIds.length > 0) {
        const adverseEventResults = await Promise.all(encounterIds.map((encId) => $httpFhirApi.get(`AdverseEvent?encounter=${encId}`)));
        adverseEventResults.forEach((res) => {
            const validAEs = Array.isArray(res.data) ? res.data.filter((ae) => ae != null) : [];
            validAEs.forEach((ae) => {
                if (!transaction.find((e) => e.request.url.includes(ae.id) && e.request.method === 'DELETE')) {
                    transaction.push(crud.delete(ae));
                }
            });
        });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const endEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let encounterResources;
    let resourcesToUpdate;
    let encounterToQuery;
    if (data.groupEncounterIds?.length) {
        const ids = [...data.groupEncounterIds];
        if (data.appointmentEncounter?.id && !ids.includes(data.appointmentEncounter.id)) {
            ids.push(data.appointmentEncounter.id);
        }
        encounterToQuery = 'id=' + ids.join(',');
    } else if (data.appointmentEncounter?.appointment?.[0]?.id) {
        encounterToQuery = 'appointment=' + data.appointmentEncounter.appointment[0].id;
    } else {
        encounterToQuery = 'id=' + (data.appointmentEncounter?.id || data.encounter.id);
    }

    encounterResources = await $httpFhirApi.get(
        '/Encounter?' +
            encounterToQuery +
            '&_revinclude=Procedure:encounter,Task:encounter,Encounter:part-of,QuestionnaireResponse:encounter&_elements=Task.focus,Task.status,QuestionnaireResponse.status,Encounter.period,Encounter.status,Procedure.status',
    );

    // update placeholder task - ignore tasks with a focus - we want to leave them alone
    resourcesToUpdate = encounterResources.data.filter((item) => {
        return !((item.resourceType === 'Task' && item.focus) || item.resourceType === 'QuestionnaireResponse');
    });

    // make sure we exclude the forms that are being locked concurrently in data.formsToLock
    let incompleteForms = encounterResources.data.filter((e) => e.resourceType === 'QuestionnaireResponse' && e.status !== 'completed' && !data.formsToLock?.some((f) => f.id === e.id));

    if (incompleteForms.length && !data.fromCalendarModal) {
        // If this is the appointment encounter, only update the appointment encounter
        if (data.encounter.id === data.appointmentEncounter.id) {
            resourcesToUpdate = [];
        } else {
            resourcesToUpdate = encounterResources.data.filter((resource) => resource.resourceType === 'Encounter' && resource.id === data.encounter.id);
        }
    } else if (data.fromCalendarModal) {
        // If from calendar modal, mark all encounters and in-progress procedures as finished
        resourcesToUpdate = encounterResources.data.filter((resource) => resource.resourceType === 'Encounter' || (resource.resourceType === 'Procedure' && resource.status === 'in-progress'));
    }

    if (!resourcesToUpdate?.length) return;
    let enc = resourcesToUpdate.find((resource) => resource.resourceType === 'Encounter');
    let end = '';

    if (enc) {
        if (data.defaultDuration) {
            let startTime = new Date(enc.period?.start);
            end = addMinutes(startTime, data.defaultDuration).toISOString();
        } else {
            end = enc.period?.end || new Date().toISOString();
        }
    }

    resourcesToUpdate.forEach((resource) => {
        if (resource.resourceType === 'Encounter') {
            resource.status = 'finished';

            if (!resource.period) {
                resource.period = { start: enc?.period?.start };
            }

            if (!resource.period.end) {
                resource.period.end = timezoneApi.getLocalDateTime(end);
            }
        } else {
            resource.status = 'completed';
        }
        transaction.push(crud.patch(resource));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const reopenEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    let encounterToQuery = data.appointmentEncounter?.appointment?.[0]?.id ? 'appointment=' + data.appointmentEncounter.appointment[0].id : 'id=' + data.encounter.id;

    let result = await $httpFhirApi.get('/Encounter?' + encounterToQuery + '&_revinclude=Procedure:encounter,Task:encounter&_include=Encounter:part-of:Encounter');
    // update placeholder task - ignore tasks with a focus - we want to leave them alone
    let encounters = result.data.filter((item) => {
        return !(item.resourceType === 'Task' && item.focus) && item.resourceType !== 'QuestionnaireResponse';
    });

    encounters.forEach((resource) => {
        if (resource.resourceType === 'Encounter' || resource.resourceType === 'Procedure') {
            resource.status = 'in-progress';
        }
        transaction.push(crud.patch(resource));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const addService = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // Check for non-billable service (id === 'non-billable') or regular service (has officeCode)
    const isNonBillable = data.service?.id === 'non-billable';
    if (!isNonBillable && !data.service?.officeCode) {
        return {};
    }
    let encounters = [data.encounter];
    // apply changes to all encounters associated with the appointment
    if (data.appointment?.id) {
        encounters = (await $httpFhirApi.get('/Encounter?appointment=' + data.appointment.id)).data.filter((encounter) => encounter.type?.[0]?.text === 'formEncounter');
    }
    const { getReference } = useWorkflow();
    encounters.forEach((encounter) => {
        const status = encounter.status === 'finished' ? 'completed' : 'in-progress';
        const referenceEncounter = getReference(encounter);
        const subject = encounter.subject;
        const newProcedureId = uuidv4();

        // Handle non-billable services to match format used at appointment start
        let newProcedure;

        if (isNonBillable) {
            // Non-billable procedures match the format from addProcedure: no code field, no unit, perUnitDuration: 0
            newProcedure = {
                id: newProcedureId,
                status: status,
                encounter: referenceEncounter,
                subject: subject,
                code: {
                    coding: [
                        {
                            display: 'Non-billable',
                            version: 'non-billable',
                        },
                    ],
                },
                perUnitDuration: 0,
            };
        } else {
            newProcedure = {
                id: newProcedureId,
                status: status,
                encounter: referenceEncounter,
                subject: subject,
                code: {
                    text: data.service.description,
                    coding: [
                        {
                            code: data.service.billingCode,
                            display: data.service.officeCode,
                            version: data.service.id,
                        },
                    ],
                },
                unit: data.service.unit,
                modifier: data.service.modifier,
                perUnitDuration: data.service.perUnitDuration,
            };
        }

        let procedure = new Procedure(newProcedure);
        transaction.push(crud.post(procedure));
        // sub-procedures (addOn services)
        data.service.addOn?.forEach((addOn) => {
            const subProcedure = {
                id: uuidv4(),
                status: status,
                encounter: referenceEncounter,
                subject: subject,
                partOf: [{ id: newProcedureId, resourceType: 'Procedure' }],
                code: { text: addOn.temp.description, coding: [{ code: addOn.temp.billingCode, display: addOn.temp.officeCode, version: addOn.addOnCode.id }] },
                unit: addOn.unit,
            };
            let subResource = new Procedure(subProcedure);
            transaction.push(crud.post(subResource));
        });
        // update encounter resource
        transaction.push(crud.patch(encounter));
    });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const updateService = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];
    let procedures;

    // Handle both group and individual encounter cases
    if (data.procedures) {
        procedures = data.procedures;
    } else {
        procedures = (await $httpFhirApi.get('/Procedure?id=' + data.procedure.id + '&code=' + data.service.billingCode + '&_revinclude=Procedure:part-of')).data;
    }

    // remove inactive addOns
    procedures
        ?.filter((item) => item.partOf?.length)
        ?.forEach((a) => {
            const subProcHsId = a.code.coding?.[0]?.version;
            const kept = data.service.addOn?.some((b) => subProcHsId === b.addOnCode?.id);
            if (kept) return;

            transaction.push(crud.delete(a));
        });

    // process changes to existing procedures and update subProcedures
    procedures
        ?.filter((item) => !item.partOf?.length)
        .forEach((procedure) => {
            // update units and modifiers
            procedure.unit = data.service.unit;
            // Ensure modifiers are in the correct format
            procedure.modifier = data.service.modifier.map((mod) => ({
                code: mod.code,
                position: mod.position,
                includeByDefault: mod.includeByDefault,
            }));
            procedure.perUnitDuration = data.service.perUnitDuration;
            transaction.push(crud.patch(procedure));

            // sub-procedures (addOn services)
            data.service.addOn?.forEach((addOn) => {
                const addOnHsId = addOn.addOnCode?.id;
                // ignore addOns that already exist (match by HealthcareService ID, not billing code)
                const alreadyExists = procedures.filter((item) => item.partOf?.length).some((b) => b.code.coding?.[0]?.version === addOnHsId);
                if (alreadyExists) return;

                if (!addOn.temp) return;

                // add missing addOns
                const subProcedure = {
                    id: uuidv4(),
                    status: procedure.status,
                    encounter: procedure.encounter,
                    subject: procedure.subject,
                    partOf: [{ id: procedure.id, resourceType: 'Procedure' }],
                    code: {
                        text: addOn.temp.officeCode,
                        coding: [{ code: addOn.temp.billingCode, display: addOn.temp.officeCode, version: addOn.addOnCode.id }],
                    },
                    // Commenting out for now after talking with Jayte
                    // officeCode: addOn.temp.officeCode,
                    description: addOn.temp.description,
                    unit: addOn.unit,
                };

                let subResource = new Procedure(subProcedure);
                transaction.push(crud.post(subResource));
            });
        });

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeService = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (data.appointment) {
        // Get all encounters and procedures for the appointment
        const encounters = await $httpFhirApi.get(`/Encounter?appointment=${data.appointment.id}`);
        const formEncounters = encounters.data.filter((enc) => enc.type?.[0]?.text === 'formEncounter');

        const procedures = await $httpFhirApi.get(`/Procedure?encounter=${formEncounters.map((e) => e.id).join(',')}`);

        // Delete matching procedures
        procedures.data
            .filter((procedure) => procedure.code.coding[0].display === data.service.code.coding[0].display)
            .forEach((procedure) => {
                transaction.push(crud.delete(procedure));
            });
    } else {
        // Individual encounter service
        let procedures = await $httpFhirApi.get('/Procedure?id=' + data.procedure.id + '&_elements=id&_revinclude=Procedure:part-of');
        procedures.data.forEach((procedure) => {
            if (procedure.resourceType === 'Procedure') {
                transaction.push(crud.delete(procedure));
            }
        });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const addForm = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    if (!data.form) {
        return { transaction };
    }

    const isPortal = data.isPortal || false;

    const { addFormTask } = useAppointmentFlow();

    let encounters = data.encounters || [data.encounter];
    let isPortalPacket = encounters?.[0]?.type?.[0]?.coding?.[0]?.code === 'portalPacket';

    // add a new form and make appropriate assignments
    let assignee = data.appointment?.participant?.filter((item) => item.actor.resourceType === 'Practitioner')?.map((item) => item.actor) || [];
    const start = new Date().toISOString();
    const end = addDays(new Date(), 7).toISOString();

    let mainTaskId;
    let groupEncounterTaskId = data.groupTaskId;

    if (data.isGroupEncounter) {
        mainTaskId = uuidv4();

        transaction = await addFormTask(
            {
                task: {
                    id: mainTaskId,
                    owner: getProvider(encounters[0]),
                    start: start,
                    end: end,
                    assignee: assignee,
                    groupTask: groupEncounterTaskId,
                    parentGroupTask: groupEncounterTaskId,
                },
                form: data.form,
                appointment: data.appointment,
            },
            transaction,
        );
    }

    for (let encounter of encounters) {
        let owner = getProvider(encounter);
        if (isPortalPacket) {
            assignee = [encounter.subject];
            owner = encounter.subject;

            if (data.filler) {
                assignee = [data.filler];
                owner = data.filler;
            }
        }

        if (!assignee.length) {
            assignee = [getProvider(encounter)];
        }

        transaction = await addFormTask(
            {
                task: {
                    owner: owner,
                    for: encounter.subject,
                    start: start,
                    end: end,
                    assignee: assignee,
                    groupTask: groupEncounterTaskId,
                    groupParticipantTask: mainTaskId,
                },
                form: data.form,
                appointment: data.appointment,
                encounter: encounter,
            },
            transaction,
        );
    }

    if (!isPortal) {
        // remove any empty tasks now that there is a form task in the encounter
        const emptyTask = await $httpFhirApi.get('/Task?encounter=' + encounters.map((item) => item.id).join(',') + '&focus:missing=true');

        for (let item of emptyTask.data) {
            transaction = await deleteTask(item.id, transaction);
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

const removeForm = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // return if form is in progress
    if (!data.form || data.form.status === 'completed') {
        return transaction;
    }

    const encounterIds = data.encounters.map((item) => item.id);

    // delete tasks,  have to get based-on:Task to work for group notes
    let tasks = await $httpFhirApi.get(
        'Task?encounter=' + encounterIds.join(',') + '&code=form&_include=based-on:Task,Task:focus,Task:encounter&_revinclude=Encounter:part-of,QuestionnaireResponse:encounter',
    );
    const allEncounters = [...tasks.data.filter((item) => item.resourceType === 'Encounter'), ...data.encounters];
    const questionnaireResponse = tasks.data.find((item) => item.resourceType === 'QuestionnaireResponse' && item.id === data.form.questionnaireResponse);
    const questionnaireResponseEncounter = allEncounters.find(
        (item) => item.resourceType === 'Encounter' && item.id === questionnaireResponse?.encounter?.id && item.type?.[0]?.text === 'formEncounter',
    );

    let questionnaireResponseIds = [
        ...new Set([...(questionnaireResponse?.id ? [questionnaireResponse.id] : []), ...tasks.data.filter((item) => item.resourceType === 'QuestionnaireResponse' && item.id).map((item) => item.id)]),
    ];

    if (questionnaireResponseEncounter) {
        transaction.push(crud.delete(questionnaireResponseEncounter));
    }

    // Delete tasks (deleteTask also deletes QuestionnaireResponse tied to task and assignments)
    for (const task of tasks.data.filter((item) => item.resourceType === 'Task' && item.code.coding[0].version.includes(data.form.url))) {
        transaction = await deleteTask(task.id, transaction);
    }

    // Cleanup finalizedInForm properties for resources that were finalized in the deleted forms
    if (questionnaireResponseIds.length > 0) {
        // Get all unique patient IDs from encounters for cleanup (only needed for group forms with multiple patients)
        const allPatientIds = [
            ...allEncounters.filter((item) => item.resourceType === 'Encounter' && item.subject?.id).map((item) => item.subject.id),
            ...(data.encounters?.map((enc) => enc.subject?.id).filter(Boolean) || []),
        ];
        const uniquePatientIds = [...new Set(allPatientIds)];

        if (uniquePatientIds.length > 0) {
            // If an individual encounter, call directly
            if (uniquePatientIds.length === 1) {
                const cleanupResult = await cleanupFinalizedInForm(questionnaireResponseIds, uniquePatientIds[0], []);
                transaction.push(...cleanupResult);
            } else {
                // Multiple patients (group form) - process in parallel
                const cleanupResults = await Promise.all(uniquePatientIds.map((patientId) => cleanupFinalizedInForm(questionnaireResponseIds, patientId, [])));
                cleanupResults.forEach((result) => {
                    transaction.push(...result);
                });
            }
        }
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

async function removeGroupAdhocEncounter(data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Query for all resources in the adhoc group encounter
    // This gets all tasks, encounters, procedures, and questionnaire responses linked to the group
    const resources = await $httpFhirApi.get(
        `Task?groupTask=${data.groupTaskId}&_include=Task:focus:QuestionnaireResponse&_include=Task:encounter:Encounter&_revinclude=Encounter:part-of:Encounter&_revinclude=Procedure:encounter:Encounter`,
    );

    const allResources = resources.data;
    const deletedIds = new Set();

    // Helper to add delete operation if not already added
    const addDelete = (resource) => {
        if (!deletedIds.has(resource.id)) {
            deletedIds.add(resource.id);
            transaction.push(crud.delete(resource));
        }
    };

    // Collect clinical impression queries for form tasks with questionnaire responses
    const isFormTaskWithQR = (resource) =>
        resource.resourceType === 'Task' && resource.focus?.id && resource.focus.resourceType === 'QuestionnaireResponse' && resource.code?.coding?.[0]?.code === 'form' && resource?.for?.id;
    const formTasksWithQR = allResources.filter(isFormTaskWithQR);

    const clinicalImpressionResults = await Promise.all(
        formTasksWithQR.map((resource) => $httpFhirApi.get('ClinicalImpression?subject=' + resource.for.id + '&supportingInfo=' + resource.focus.id).then((result) => result.data || [])),
    );

    // Add clinical impressions to transaction
    clinicalImpressionResults.flat().forEach((clinicalImpression) => {
        addDelete({ id: clinicalImpression.id, resourceType: 'ClinicalImpression' });
    });

    // Collect unique questionnaire responses (from both direct resources and task focus references)
    const questionnaireResponseIds = new Set();
    allResources.forEach((resource) => {
        if (resource.resourceType === 'QuestionnaireResponse') {
            questionnaireResponseIds.add(resource.id);
        }
        if (resource.focus?.id && resource.focus.resourceType === 'QuestionnaireResponse') {
            questionnaireResponseIds.add(resource.focus.id);
        }
    });

    // Add all resources to transaction (excluding questionnaire responses which need special handling)
    allResources.forEach((resource) => {
        if (resource.resourceType !== 'QuestionnaireResponse') {
            addDelete(resource);
        }
    });

    // Process questionnaire responses sequentially (they modify transaction, so must be sequential)
    const questionnaireResponses = allResources.filter((item) => item.resourceType === 'QuestionnaireResponse' && questionnaireResponseIds.has(item.id));
    const { removeObservations, cleanupChargeItemsForQuestionnaireResponse } = useQuestionnaireFlow();

    // Use reduce to process sequentially without await in for loop
    transaction = await questionnaireResponses.reduce(async (txnPromise, qr) => {
        let txn = await txnPromise;
        txn = await removeObservations(qr, txn);
        txn = await cleanupChargeItemsForQuestionnaireResponse(qr.id, null, txn);
        return txn;
    }, Promise.resolve(transaction));

    // Delete AdverseEvents linked to any encounter being removed
    const encounterIds = [
        ...new Set(
            allResources
                .filter((r) => r.resourceType === 'Encounter')
                .map((r) => r.id)
                .filter(Boolean),
        ),
    ];
    if (encounterIds.length > 0) {
        const adverseEventResults = await Promise.all(encounterIds.map((encId) => $httpFhirApi.get(`AdverseEvent?encounter=${encId}`)));
        adverseEventResults.forEach((res) => {
            const validAEs = Array.isArray(res.data) ? res.data.filter((ae) => ae != null) : [];
            validAEs.forEach((ae) => addDelete(ae));
        });
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
}

const getProvider = function (Encounter) {
    const item = Encounter.participant?.find((p) => p.type?.[0]?.coding?.[0]?.code === 'PPRF');
    return item?.individual || undefined;
};
export function useEncounterFlow() {
    return {
        beginAdhocEncounter,
        beginAdhocGroupEncounter,
        addParticipantToGroupEncounter,
        createPortalPacket,
        updateEncounter,
        removeEncounter,
        removeGroupAdhocEncounter,
        endEncounter,
        reopenEncounter,
        addService,
        updateService,
        removeService,
        addForm,
        removeForm,
        removeParticipantFromGroupEncounter,
    };
}
